// src/pages/Compras/CentrosCosto.js
//
// Equivalencias de centros de costo entre CIMSA y el resto de las empresas.
//
// Vive en la tabla CC_Empresas de MariaDB, no en SAP: es un mapeo propio que
// usa el importador de intercompany para traducir el CC que viene en el Excel
// de CIMSA al centro de costo de la empresa destino.
//
// Estaba escondido dentro del importador. Se separó porque es configuración: se
// carga una vez y se consulta, no forma parte del flujo de importar.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast/ToastProvider';
import './CentrosCosto.css';

// Las mismas que valida el backend. CIMSA no está: es el origen de la
// equivalencia, no un destino.
const EMPRESAS = ['CODYLSA', 'DYLASA', 'TDU', 'MALARGUE'];

const VACIO = { empresa: '', cc_cimsa: '', equivalencia: '' };

const CentrosCosto = () => {
  const { showToast } = useToast();
  // Ver la tabla lo puede hacer cualquiera que opere; modificarla, sólo quien
  // configura. El backend aplica lo mismo.
  const { puedeConfigurar } = useAuth();

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroCC, setFiltroCC] = useState('');

  const [modal, setModal] = useState(null);   // { modo, empresa, cc_cimsa, equivalencia, original }
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async (empresa) => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/compras/cc-empresas', {
        params: empresa ? { empresa } : {}
      });
      setFilas(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las equivalencias.');
    } finally {
      setCargando(false);
    }
  }, []);

  // El filtro de empresa lo resuelve el backend; el de CC, el navegador.
  useEffect(() => { cargar(filtroEmpresa); }, [cargar, filtroEmpresa]);

  const visibles = filas.filter(f => {
    if (!filtroCC.trim()) return true;
    const t = filtroCC.trim().toLowerCase();
    return String(f.CC_CIMSA).toLowerCase().includes(t) ||
           String(f.Equivalencia).toLowerCase().includes(t);
  });

  const abrirNuevo = () => setModal({ modo: 'nuevo', ...VACIO, empresa: filtroEmpresa || '' });

  const abrirEdicion = (f) => setModal({
    modo: 'editar',
    empresa: f.empresa,
    cc_cimsa: f.CC_CIMSA,
    equivalencia: f.Equivalencia,
    original: { empresa: f.empresa, cc_cimsa: f.CC_CIMSA }
  });

  const guardar = async () => {
    const payload = {
      empresa: modal.empresa,
      cc_cimsa: String(modal.cc_cimsa || '').trim(),
      equivalencia: String(modal.equivalencia || '').trim()
    };

    if (!payload.empresa || !payload.cc_cimsa || !payload.equivalencia) {
      showToast('Completá empresa, CC de CIMSA y equivalencia.', 'warning');
      return;
    }

    setGuardando(true);
    try {
      if (modal.modo === 'editar') {
        await api.put('/compras/cc-empresas', { ...payload, original: modal.original });
        showToast('Equivalencia modificada.', 'success');
      } else {
        await api.post('/compras/cc-empresas', payload);
        showToast('Equivalencia agregada.', 'success');
      }
      setModal(null);
      cargar(filtroEmpresa);
    } catch (err) {
      // El 409 del backend avisa que esa empresa + CC ya está cargada.
      showToast(err.response?.data?.error || 'No se pudo guardar.', 'danger', 9000);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="container-fluid centros-costo">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4">
        <div>
          <h1 className="mb-1">
            <i className="bi bi-diagram-3 me-2"></i>
            Centros de Costo
          </h1>
          <p className="text-muted small mb-0">
            Equivalencia entre el CC de CIMSA que viene en el Excel y el centro de costo de cada empresa.
          </p>
        </div>
        {puedeConfigurar && (
          <button className="btn btn-primary" onClick={abrirNuevo}>
            <i className="bi bi-plus-lg me-1"></i>Nueva equivalencia
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger mt-4">{error}</div>}

      <div className="row mt-4">
        {/* --- Tabla --- */}
        <div className="col-lg-8 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="row g-2 align-items-end mb-3">
                <div className="col-sm-5">
                  <label className="form-label small text-muted mb-1">Empresa</label>
                  <select
                    className="form-select form-select-sm"
                    value={filtroEmpresa}
                    onChange={e => setFiltroEmpresa(e.target.value)}
                  >
                    <option value="">Todas las empresas</option>
                    {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div className="col-sm-7">
                  <label className="form-label small text-muted mb-1">CC de CIMSA o equivalencia</label>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Buscar..."
                    value={filtroCC}
                    onChange={e => setFiltroCC(e.target.value)}
                  />
                </div>
              </div>

              {cargando ? (
                <div className="text-center my-4">
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              ) : visibles.length === 0 ? (
                <p className="text-muted mb-0">
                  {filas.length === 0
                    ? 'No hay equivalencias cargadas todavía.'
                    : 'Ninguna equivalencia coincide con el filtro.'}
                </p>
              ) : (
                <>
                  <div className="tabla-cc">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '120px' }}>Empresa</th>
                          <th>CC de CIMSA</th>
                          <th>Equivalencia en SAP</th>
                          {puedeConfigurar && <th style={{ width: '48px' }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {visibles.map(f => (
                          <tr key={`${f.empresa}-${f.CC_CIMSA}`}>
                            <td><span className="badge text-bg-light border">{f.empresa}</span></td>
                            <td><code className="small">{f.CC_CIMSA}</code></td>
                            <td>{f.Equivalencia}</td>
                            {puedeConfigurar && (
                              <td className="text-end">
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => abrirEdicion(f)}
                                  title="Modificar"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-muted small mb-0 mt-2">
                    {visibles.length}
                    {visibles.length !== filas.length && ` de ${filas.length}`} equivalencias
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* --- Explicación --- */}
        <div className="col-lg-4 mb-3">
          <div className="card shadow-sm h-100 card-ayuda">
            <div className="card-body">
              <h6 className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Cómo dar de alta una equivalencia
              </h6>

              <p className="small">
                El importador de intercompany recibe un Excel de <strong>CIMSA</strong>. Cada
                línea trae un centro de costo de CIMSA, pero la orden se crea en otra
                empresa, donde ese centro se llama distinto. Esta tabla es la traducción.
              </p>

              <ol className="small ps-3 mb-3">
                <li className="mb-2">
                  <strong>Empresa:</strong> a cuál se le va a crear la orden.
                  CIMSA no está en la lista porque es el origen.
                </li>
                <li className="mb-2">
                  <strong>CC de CIMSA:</strong> el código tal cual viene en la columna
                  del Excel. Tiene que coincidir exactamente.
                </li>
                <li className="mb-2">
                  <strong>Equivalencia:</strong> el centro de costo que corresponde en
                  la empresa destino, tal cual está en SAP.
                </li>
              </ol>

              <div className="alert alert-warning py-2 small mb-3">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Si el importador no encuentra la equivalencia, <strong>saltea esa
                línea sin avisar</strong> y la orden se crea incompleta. Por eso
                conviene cargarlas antes de importar.
              </div>

              <p className="small text-muted mb-0">
                No se puede repetir la misma empresa con el mismo CC de CIMSA: cada
                combinación tiene una sola equivalencia.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- Modal de alta / edición --- */}
      {modal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modal.modo === 'editar' ? 'Modificar equivalencia' : 'Nueva equivalencia'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setModal(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Empresa</label>
                    <select
                      className="form-select"
                      value={modal.empresa}
                      onChange={e => setModal({ ...modal, empresa: e.target.value })}
                    >
                      <option value="">Elegir empresa...</option>
                      {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">CC de CIMSA</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Tal cual viene en el Excel"
                      value={modal.cc_cimsa}
                      onChange={e => setModal({ ...modal, cc_cimsa: e.target.value })}
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label">Equivalencia en SAP</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Centro de costo de la empresa destino"
                      value={modal.equivalencia}
                      onChange={e => setModal({ ...modal, equivalencia: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && guardar()}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setModal(null)} disabled={guardando}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
                    {guardando
                      ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</>
                      : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setModal(null)}></div>
        </>
      )}
    </div>
  );
};

export default CentrosCosto;
